/**
 * Degrees and departments — the enrolment model, as DATA rather than code.
 *
 * These used to be a constant in modules.js, which meant creating a degree
 * required an engineer and a deploy. They now live in Firestore
 * (`config/degrees`, `config/departments`) and are edited in the admin pages;
 * what remains here is the shape, the defaults used to seed an empty install,
 * and the pure resolution logic, so the interesting part stays testable in
 * plain node.
 *
 * The model, in one line: a DEPARTMENT confers DEGREES, a degree contains
 * COURSES, and a course contains modules.
 *
 * Nothing here gates anything. Enrolment decides what a trainee is expected to
 * do — what leads their home page and what their score is measured against —
 * and every module remains open to anyone who goes looking. See
 * SECURITY-MODEL.md.
 */

import { SEED_COURSES, modules as builtInModules } from './modules.js'
import { isCourseVisible, mergeCatalogue, requiredInCourse } from './catalogue.js'

/** Seed data for a fresh install; overwritten by whatever the admin saves. */
export const DEFAULT_DEGREES = [
  {
    id: 'finance',
    name: 'Finance',
    description: 'Get Claude working on spreadsheets, and automate the parts worth automating.',
    tracks: ['claude-basics', 'ai-finance', 'apps-script'],
  },
  {
    id: 'client-services',
    name: 'Client Services',
    description: 'Build clean reports from raw data, with Claude alongside you.',
    tracks: ['claude-basics', 'sheets'],
  },
]

export const DEFAULT_DEPARTMENTS = [
  { id: 'finance', name: 'Finance', degrees: ['finance'] },
  { id: 'client-services', name: 'Client Services', degrees: ['client-services'] },
]

/** A url-safe id from a name, for degrees and departments created in the UI. */
export function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/** Look a degree up in a given list. */
export function findDegree(degreeList, id) {
  return (degreeList || []).find((d) => d.id === id) || null
}

export function findDepartment(departmentList, id) {
  return (departmentList || []).find((d) => d.id === id) || null
}

/**
 * Every course belonging to these degrees, without repeats and in the order
 * courses are declared, so two degrees sharing a course produce one entry.
 */
export function coursesForDegrees(degreeIds = [], degreeList = DEFAULT_DEGREES, allCoursesList = SEED_COURSES, customModulesList = []) {
  const wanted = new Set(
    (degreeIds || []).flatMap((id) => findDegree(degreeList, id)?.tracks || [])
  )
  return allCoursesList
    .filter((t) => wanted.has(t.id) && isCourseVisible(t, customModulesList))
    .map((t) => t.id)
}

/**
 * The modules a degree is marked out of: published, non-optional, across all
 * of its courses.
 *
 * Supports both standard track modules and AI-studio generated custom modules.
 */
export function degreeRequiredModules(degreeId, degreeList = DEFAULT_DEGREES, allCoursesList = SEED_COURSES, customModulesList = []) {
  const degree = findDegree(degreeList, degreeId)
  if (!degree) return []

  const courseIds = coursesForDegrees([degreeId], degreeList, allCoursesList, customModulesList)
  const catalogue = mergeCatalogue(builtInModules, customModulesList || [])
  // Each course's OWN ordered list, so a module in two of this degree's courses
  // is found through either — and deduplicated below, as it always was.
  const courseModules = courseIds.flatMap((courseId) => {
    const course = (allCoursesList || []).find((t) => String(t.id) === String(courseId)) || { id: courseId }
    return requiredInCourse(course, catalogue)
  })

  // Direct custom modules attached to degree if any
  const directModuleIds = new Set(degree.modules || [])
  const directModules = customModulesList.filter(
    (m) => directModuleIds.has(m.id) && m.status === 'published' && !m.optional
  )

  const combined = [...courseModules, ...directModules]
  const seen = new Set()
  return combined.filter((m) => {
    const key = String(m.id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Every degree a person holds: the ones their department confers, plus any
 * awarded to them individually.
 *
 * A union rather than a choice. Conferring by department covers almost
 * everybody and updates itself when the mapping changes; the individual list
 * is the exception — somebody in Finance who also needs the Client Services
 * degree — and neither can quietly cancel the other.
 */
export function degreesForPerson({ department = null, degrees: awarded = [] } = {}, departmentList = DEFAULT_DEPARTMENTS, degreeList = DEFAULT_DEGREES) {
  const conferred = findDepartment(departmentList, department)?.degrees || []
  const held = new Set([...conferred, ...(awarded || [])])
  return (degreeList || []).filter((d) => held.has(d.id)).map((d) => d.id)
}

/** Degrees a department confers that no longer exist — an admin-page warning. */
export function danglingDegrees(departmentList = [], degreeList = []) {
  const known = new Set((degreeList || []).map((d) => d.id))
  return (departmentList || []).flatMap((dept) =>
    (dept.degrees || [])
      .filter((id) => !known.has(id))
      .map((id) => ({ department: dept.id, degree: id }))
  )
}

/** Courses a degree claims that no longer exist, or have nothing published. */
export function danglingCourses(degreeList = [], allCoursesList = SEED_COURSES, customModulesList = []) {
  const known = new Set([
    ...allCoursesList.map((t) => t.id),
    ...customModulesList.map((m) => m.track).filter(Boolean),
  ])
  return (degreeList || []).flatMap((degree) =>
    (degree.tracks || [])
      .filter((id) => !known.has(id))
      .map((id) => ({ degree: degree.id, track: id }))
  )
}
